import { useCallback, useEffect, useMemo, useRef } from 'react';

import Animated from 'react-native-reanimated';
import {
  Chip,
  Colors,
  Flex,
  List,
  Typography,
  useScreenSize,
  type ListRef,
  RemoteImage,
  HorizontalSpacing,
} from '@banhala/ui-kit';
import type { RadioFilter, RadioFilterItem } from '../types';
import type { AnyObject } from '../../../../../types';
import { useFilterListAtom } from '../Provider/filterList/useFilterListAtom';
import { useLogClickFilter } from '../hooks/useLogClickFilter';
import { useLogApplyFilter } from '../hooks/useLogApplyFilter';
import { useWebDragToScroll } from '../../../../../hooks';

// const AnimatedView = Animated.createAnimatedComponent(View);
const AnimatedList = Animated.createAnimatedComponent(List<RadioFilterItem>);

type HorizontalRadioListProps = {
  radioFilter: RadioFilter;
  logging: AnyObject;
  scrollToFilterList: VoidFunction;
  /** sub_radio 그룹 여부 — true면 CLICK_SUB_FILTER, false면 CLICK_FILTER */
  isSubRadio?: boolean;
  /**
   * 이 라디오를 선택할 때 함께 남길 다른 필터의 query_name 목록.
   * 여기에 없는 필터는 모두 제거된다(자기 query_name 은 항상 보존).
   * radio/sub_radio 중 무엇을 남길지는 둘을 다 아는 부모(FilterList)가 정한다.
   */
  preservedQueryNames?: string[];
};

export const HorizontalRadioList = ({
  radioFilter,
  logging,
  scrollToFilterList,
  isSubRadio = false,
  preservedQueryNames,
}: HorizontalRadioListProps) => {
  const { setRadioFilter, setFixedRadioFilter } = useFilterListAtom();
  const logClickFilter = useLogClickFilter(logging);
  const logApplyFilter = useLogApplyFilter(logging);

  const isFirstRenderRef = useRef(true);
  const radioListRef = useRef<ListRef<RadioFilterItem>>(null);
  // web(데스크톱) 마우스 drag-to-scroll: 기존 scrollToIndex 용 ref 에 훅만 연결(터치/네이티브 no-op).
  useWebDragToScroll(radioListRef);

  const { width: screenWidth } = useScreenSize();
  const scrollToItemTargetPosition = useMemo(() => {
    return screenWidth * 0.5 - 20;
  }, [screenWidth]);

  const selectedIndex = useMemo(() => {
    return radioFilter.possible_items.findIndex(
      item => item.query_value === radioFilter.selected_value,
    );
  }, [radioFilter]);

  const scrollToIndex = useCallback(
    (index: number, animated: boolean) => {
      radioListRef.current?.scrollToIndex({
        index,
        animated,
        viewOffset: scrollToItemTargetPosition,
      });
    },
    [scrollToItemTargetPosition],
  );

  const scrollToSelectedItem = useCallback(
    (animated: boolean) => {
      if (selectedIndex >= 0) {
        scrollToIndex(selectedIndex, animated);
      }
    },
    [selectedIndex, scrollToIndex],
  );

  const onScrollToIndexFailed = useCallback(
    (info: { index: number }) => {
      requestAnimationFrame(() => {
        scrollToIndex(info.index, false);
      });
    },
    [scrollToIndex],
  );

  // 첫 렌더링 시 컨텐츠가 렌더링된 후 스크롤
  const onContentSizeChange = useCallback(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      scrollToSelectedItem(false);
    }
  }, [scrollToSelectedItem]);

  // 필터 변경 시 스크롤 (첫 렌더링 이후)
  useEffect(() => {
    if (!isFirstRenderRef.current) {
      scrollToSelectedItem(true);
    }
  }, [radioFilter, scrollToSelectedItem]);

  const handleChangeFilter = useCallback(
    (item: RadioFilterItem) => {
      logClickFilter({ itemLogging: item.logging, isSubRadio });
      logApplyFilter({ itemLogging: item.logging });
      scrollToFilterList();
      // 지정된 것만 남기고 나머지 필터는 제거된다(setRadioFilter 가 params 를 한 번에 갱신).
      setRadioFilter({
        queryName: radioFilter.query_name,
        queryValue: item.query_value,
        preservedQueryNames,
      });
    },
    [
      logClickFilter,
      isSubRadio,
      logApplyFilter,
      scrollToFilterList,
      preservedQueryNames,
      setRadioFilter,
      radioFilter.query_name,
    ],
  );

  const isSecondaryTab = radioFilter.view_type === 'SECONDARY_TAB';
  const isChip = radioFilter.view_type === 'CHIP';
  const hasFixedRow = (radioFilter.fixed_toggles ?? []).length > 0;

  const contentContainerStyle = useMemo(
    () => ({
      paddingHorizontal: hasFixedRow ? 8 : 16,
      gap: isChip ? 8 : isSecondaryTab ? 8 : 20,
      ...(isChip && { paddingVertical: 12 }),
    }),
    [hasFixedRow, isChip, isSecondaryTab],
  );

  const renderItem = ({
    item,
    index,
  }: {
    item: RadioFilterItem;
    index: number;
  }) => {
    const { text, query_value } = item;
    const isSelected = query_value === radioFilter.selected_value;
    const testID = `FILTER_LIST?LIST_INDEX=${index}&VALUE=${text}`;

    if (isChip) {
      return (
        <Chip
          testID={testID}
          size="medium"
          state={isSelected ? 'selected' : 'enabled'}
          onPress={() => {
            handleChangeFilter(item);
          }}
        >
          {text}
        </Chip>
      );
    }

    const selectedTextColor =
      radioFilter.render?.selected_text_color ?? Colors.content_primary;
    const textColor =
      radioFilter.render?.text_color ?? Colors.content_secondary;
    return (
      <Flex
        testID={testID}
        backgroundColorValue={radioFilter.render?.background_color}
        borderBottomWidth={!isSecondaryTab && isSelected ? 2 : 0}
        padding={{ horizontal: isSecondaryTab ? 4 : 2, vertical: 13 }}
        onPress={() => {
          handleChangeFilter(item);
        }}
      >
        <Typography
          type={isSecondaryTab || isSelected ? 'subtitle2' : 'body2'}
          rawColor={isSelected ? selectedTextColor : textColor}
        >
          {text}
        </Typography>
      </Flex>
    );
  };

  return (
    <Flex.Row alignItems={'center'}>
      {(radioFilter.fixed_toggles ?? []).length > 0 && (
        <Flex.Row alignItems={'center'} padding={{ left: 16 }}>
          {(radioFilter.fixed_toggles ?? []).map((item, index) => {
            const testID = `FILTER_LIST?LIST_INDEX=${index}&VALUE=${item.text}`;
            const selectedTextColor =
              radioFilter.render?.selected_text_color ?? Colors.content_primary;
            const textColor =
              radioFilter.render?.text_color ?? Colors.content_secondary;

            const handlePressFixedToggle = () => {
              logClickFilter({ itemLogging: item.logging });
              logApplyFilter({
                itemLogging: item.logging,
                overrides: {
                  [item.logging_name]: !item.is_selected ? '1' : '0',
                },
              });
              setFixedRadioFilter({
                queryName: item.query_name,
                queryValue: item.query_value,
                isSelected: item.is_selected,
              });
            };

            if (isChip) {
              return (
                <Flex.Center
                  testID={testID}
                  key={testID}
                  onPress={handlePressFixedToggle}
                  borderColor={
                    item.is_selected
                      ? 'border_slate_primary'
                      : 'border_secondary'
                  }
                  backgroundColor={item.is_selected ? 'slate50' : 'white'}
                  borderWidth={1}
                  borderRadius={20}
                  width={36 * item.image_ratio}
                  height={36}
                >
                  <RemoteImage
                    uri={item.image}
                    width={36 * item.image_ratio}
                    height={36}
                  />
                </Flex.Center>
              );
            }

            return (
              <Flex
                testID={testID}
                backgroundColorValue={radioFilter.render?.background_color}
                borderBottomWidth={!isSecondaryTab && item.is_selected ? 2 : 0}
                padding={{ horizontal: isSecondaryTab ? 4 : 2, vertical: 13 }}
                onPress={handlePressFixedToggle}
              >
                <Typography
                  type={
                    isSecondaryTab || item.is_selected ? 'subtitle2' : 'body2'
                  }
                  rawColor={item.is_selected ? selectedTextColor : textColor}
                >
                  {item.text}
                </Typography>
              </Flex>
            );
          })}
          <HorizontalSpacing size={8} />
          <Flex height={20} width={1} backgroundColor={'gray30'} />
        </Flex.Row>
      )}

      <AnimatedList
        ref={radioListRef}
        horizontal={true}
        onScrollToIndexFailed={onScrollToIndexFailed}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={contentContainerStyle}
        data={radioFilter.possible_items}
        extraData={radioFilter.selected_value}
        renderItem={renderItem}
        onContentSizeChange={onContentSizeChange}
      />
    </Flex.Row>
  );
};
